import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaEye,
  FaTimes,
  FaMoneyBillWave,
  FaUser,
  FaCalendarAlt,
  FaSearch,
  FaChevronDown,
  FaCheck,
  FaSpinner,
} from "react-icons/fa";
import apiRequest from "../services/api";

const EmployeeAdvanceManagement = () => {
  const [advances, setAdvances] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("create");
  const [selectedAdvance, setSelectedAdvance] = useState(null);

  const [deleteTargetId, setDeleteTargetId] = useState(null);

  const [approveTargetId, setApproveTargetId] = useState(null);
  const [approveMethod, setApproveMethod] = useState("CASH");
  const [isApproving, setIsApproving] = useState(false);

  const [formData, setFormData] = useState({
    id: null,
    employeeId: "",
    amount: "",
    advanceRate: "",
    reason: "",
    status: "ACTIVE",
  });

  const [empSearch, setEmpSearch] = useState("");
  const [empDropdownOpen, setEmpDropdownOpen] = useState(false);
  const empDropdownRef = useRef(null);

  const fetchAdvances = async () => {
    setIsLoading(true);
    setError("");

    try {
      const resp = await apiRequest("payrolls/advances");
      setAdvances(resp.data || []);
    } catch (err) {
      setError(err?.message || "Failed to fetch advances.");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const resp = await apiRequest("employees");
      setEmployees(resp.data?.content || []);
    } catch {
      // non-blocking
    }
  };

  useEffect(() => {
    fetchAdvances();
    fetchEmployees();
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        empDropdownRef.current &&
        !empDropdownRef.current.contains(e.target)
      ) {
        setEmpDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredEmployees = useMemo(() => {
    const q = empSearch.trim().toLowerCase();

    if (!q) return employees;

    return employees.filter((emp) => {
      const name = (emp.fullName || emp.name || "").toLowerCase();
      const number = (emp.employeeNumber || "").toLowerCase();
      const id = String(emp.id);

      return name.includes(q) || number.includes(q) || id.includes(q);
    });
  }, [employees, empSearch]);

  const selectedEmployeeLabel = () => {
    if (!formData.employeeId) return "";

    const emp = employees.find(
      (e) => String(e.id) === String(formData.employeeId)
    );

    if (!emp) return `#${formData.employeeId}`;

    const name = emp.fullName || emp.name || `#${emp.id}`;

    return emp.employeeNumber ? `${name} (${emp.employeeNumber})` : name;
  };

  const openModal = (mode, advance = null) => {
    setModalMode(mode);
    setSelectedAdvance(advance);
    setError("");
    setEmpSearch("");
    setEmpDropdownOpen(false);
    setDeleteTargetId(null);

    if (advance) {
      setFormData({
        id: advance.id,
        employeeId: advance.employeeId || "",
        amount: advance.amount ?? "",
        advanceRate: advance.advanceRate ?? "",
        reason: advance.reason || "",
        status: advance.status || "ACTIVE",
      });
    } else {
      setFormData({
        id: null,
        employeeId: "",
        amount: "",
        reason: "",
        status: "ACTIVE",
        advanceRate: "1.0",
      });
    }

    setIsModalOpen(true);
  };

  const selectEmployee = (emp) => {
    setFormData((prev) => ({
      ...prev,
      employeeId: emp.id,
    }));

    setEmpSearch("");
    setEmpDropdownOpen(false);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setError("");

    if (modalMode === "create") {
      if (!formData.employeeId) {
        setError("Employee is required.");
        return;
      }

      if (!formData.amount || Number(formData.amount) <= 0) {
        setError("Amount must be greater than zero.");
        return;
      }

      if (!formData.advanceRate || Number(formData.advanceRate) <= 0) {
        setError(
          "Advance Rate to calculate for return amount must be greater than zero."
        );
        return;
      }
    }

    try {
      if (modalMode === "create") {
        await apiRequest("payrolls/advances", {
          method: "POST",
          body: {
            employeeId: Number(formData.employeeId),
            amount: Number(formData.amount),
            reason: formData.reason?.trim() || null,
            advanceRate: Number(formData.advanceRate),
          },
        });
      } else if (modalMode === "edit") {
        await apiRequest(`payrolls/advances/${formData.id}`, {
          method: "PUT",
          body: {
            amount: formData.amount !== "" ? Number(formData.amount) : null,
            reason: formData.reason?.trim() || null,
            status: formData.status,
            advanceRate:
              formData.advanceRate !== "" ? Number(formData.advanceRate) : null,
          },
        });
      }

      fetchAdvances();
      setIsModalOpen(false);
    } catch (err) {
      setError(err?.message || "Advance request rejected.");
    }
  };

  const openApproveModal = (id) => {
    setApproveTargetId(id);
    setApproveMethod("CASH");
    setError("");
  };

  const closeApproveModal = () => {
    if (isApproving) return;

    setApproveTargetId(null);
    setApproveMethod("CASH");
  };

  const handleApproveAdvance = async () => {
    if (!approveTargetId) return;

    setError("");
    setIsApproving(true);

    try {
      await apiRequest(
        `payrolls/advances/${approveTargetId}/approve?method=${encodeURIComponent(
          approveMethod
        )}`,
        { method: "PUT" }
      );

      setApproveTargetId(null);
      setApproveMethod("CASH");
      fetchAdvances();
    } catch (err) {
      setError(err?.message || "Advance approval failed.");
    } finally {
      setIsApproving(false);
    }
  };

  const handleDelete = async (id) => {
    setError("");

    try {
      await apiRequest(`payrolls/advances/${id}`, {
        method: "DELETE",
      });

      setDeleteTargetId(null);
      fetchAdvances();
    } catch (err) {
      setError(err?.message || "Unable to delete advance.");
    }
  };

  const formatMoney = (val) => {
    if (val == null || val === "") return "—";

    return Number(val).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const statusBadge = (status) => {
    const map = {
      DRAFT:
        "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-500/10 dark:text-amber-400 dark:border-amber-500/20",
      ACTIVE:
        "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-400 dark:border-emerald-500/20",
      RECOVERED:
        "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-500/10 dark:text-blue-400 dark:border-blue-500/20",
      CANCELLED:
        "bg-red-50 text-red-700 border-red-200 dark:bg-red-500/10 dark:text-red-400 dark:border-red-500/20",
    };

    return (
      <span
        className={`px-2.5 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wide border ${
          map[status] ||
          "bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-white/10"
        }`}
      >
        {status || "—"}
      </span>
    );
  };

  const employeeLabel = (adv) =>
    adv.employeeName || adv.employeeNumber || `#${adv.employeeId}` || "—";

  const fieldClass =
    "w-full bg-slate-50 border border-slate-200 text-slate-800 placeholder-slate-400 rounded-xl px-4 py-3 text-xs focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 dark:bg-slate-950/60 dark:border-white/10 dark:text-white dark:placeholder-slate-600 dark:focus:border-emerald-500/50 transition";

  const labelClass =
    "text-[11px] uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400 flex items-center gap-1";

  return (
    <div className="min-h-full p-4 md:p-8 font-sans bg-slate-50 text-slate-800 dark:bg-slate-950 dark:text-slate-100 relative pb-24">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white border border-slate-200 shadow-sm dark:bg-slate-900/40 dark:border-white/10 dark:shadow-xl p-5 sm:p-6 rounded-2xl">
          <div className="flex items-center gap-4">
            <div className="w-11 h-11 rounded-xl flex items-center justify-center bg-gradient-to-br from-emerald-100 to-teal-50 border border-emerald-200 text-emerald-600 dark:from-emerald-500/20 dark:to-teal-500/10 dark:border-emerald-500/30 dark:text-emerald-400">
              <FaMoneyBillWave />
            </div>

            <div>
              <h1 className="text-lg sm:text-xl font-bold tracking-tight text-slate-900 dark:text-white">
                Employee Advances
              </h1>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                Give, track and recover salary advances.
              </p>
            </div>
          </div>

          <button
            onClick={() => openModal("create")}
            className="flex items-center gap-2 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 dark:hover:bg-emerald-500 text-white text-xs font-semibold rounded-xl transition-all shadow-lg shadow-emerald-600/20 w-full sm:w-auto justify-center"
          >
            <FaPlus />
            New Advance
          </button>
        </div>

        {/* Error */}
        {error && !approveTargetId && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 dark:bg-red-500/10 dark:border-red-500/20 dark:text-red-400 text-xs font-medium">
            {error}
          </div>
        )}

        {/* Table */}
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm dark:bg-slate-900/30 dark:border-white/10 dark:shadow-2xl">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse min-w-[640px]">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-slate-500 dark:border-white/10 dark:bg-slate-950/50 dark:text-slate-400 text-[11px] font-bold tracking-wider uppercase">
                  <th className="px-4 sm:px-6 py-4">Employee</th>
                  <th className="px-4 sm:px-6 py-4">Amount</th>
                  <th className="px-4 sm:px-6 py-4">Return Rate</th>
                  <th className="px-4 sm:px-6 py-4">Balance</th>
                  <th className="px-4 sm:px-6 py-4 hidden md:table-cell">
                    Recovered
                  </th>
                  <th className="px-4 sm:px-6 py-4 hidden lg:table-cell">
                    Reason
                  </th>
                  <th className="px-4 sm:px-6 py-4">Status</th>
                  <th className="px-4 sm:px-6 py-4 text-right">Actions</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-100 dark:divide-white/5 text-slate-600 dark:text-slate-300 text-xs">
                {isLoading ? (
                  <tr>
                    <td
                      colSpan="8"
                      className="text-center py-12 text-slate-400 dark:text-slate-500"
                    >
                      <FaSpinner className="mx-auto mb-3 text-lg text-emerald-500 animate-spin" />
                      Loading advances...
                    </td>
                  </tr>
                ) : advances.length === 0 ? (
                  <tr>
                    <td
                      colSpan="8"
                      className="text-center py-12 text-slate-400 dark:text-slate-500"
                    >
                      <FaMoneyBillWave className="mx-auto mb-3 text-xl text-slate-300 dark:text-slate-700" />
                      No active advances found.
                    </td>
                  </tr>
                ) : (
                  advances.map((adv) => (
                    <tr
                      key={adv.id}
                      className="hover:bg-slate-50 dark:hover:bg-white/[0.02] transition-colors"
                    >
                      <td className="px-4 sm:px-6 py-4">
                        <div className="font-semibold text-slate-800 dark:text-white">
                          {employeeLabel(adv)}
                        </div>

                        {adv.reference && (
                          <div className="text-[10px] text-slate-400 dark:text-slate-500 mt-0.5">
                            {adv.reference}
                          </div>
                        )}
                      </td>

                      <td className="px-4 sm:px-6 py-4 font-medium text-slate-800 dark:text-white">
                        {formatMoney(adv.amount)}
                      </td>

                      <td className="px-4 sm:px-6 py-4 text-amber-600 dark:text-amber-300 font-medium">
                        {formatMoney(adv.advanceRate)}
                      </td>

                      <td className="px-4 sm:px-6 py-4 text-amber-600 dark:text-amber-300 font-medium">
                        {formatMoney(adv.balance)}
                      </td>

                      <td className="px-4 sm:px-6 py-4 hidden md:table-cell text-slate-500 dark:text-slate-400">
                        {formatMoney(adv.recovered)}
                      </td>

                      <td className="px-4 sm:px-6 py-4 hidden lg:table-cell max-w-[180px] truncate text-slate-500 dark:text-slate-400">
                        {adv.reason || "—"}
                      </td>

                      <td className="px-4 sm:px-6 py-4">
                        {statusBadge(adv.status)}
                      </td>

                      <td className="px-4 sm:px-6 py-4 text-right">
                        {deleteTargetId === adv.id ? (
                          <div className="flex items-center justify-end gap-1.5">
                            <span className="text-[10px] text-rose-500 dark:text-rose-400 font-medium mr-1 uppercase tracking-tighter">
                              Confirm?
                            </span>

                            <button
                              type="button"
                              onClick={() => handleDelete(adv.id)}
                              className="p-1.5 bg-rose-50 text-rose-600 border border-rose-200 rounded-md hover:bg-rose-600 hover:text-white dark:bg-rose-500/10 dark:text-rose-400 dark:border-rose-500/30 dark:hover:bg-rose-600 dark:hover:text-white transition-all"
                              title="Delete permanently"
                            >
                              <FaCheck className="text-[10px]" />
                            </button>

                            <button
                              type="button"
                              onClick={() => setDeleteTargetId(null)}
                              className="p-1.5 bg-slate-100 text-slate-500 rounded-md hover:text-slate-800 dark:bg-slate-800 dark:text-slate-400 dark:hover:text-white transition-colors"
                              title="Cancel"
                            >
                              <FaTimes className="text-[10px]" />
                            </button>
                          </div>
                        ) : (
                          <div className="flex justify-end gap-1.5 sm:gap-2">
                            <button
                              type="button"
                              onClick={() => openModal("view", adv)}
                              className="p-2 bg-slate-100 hover:bg-slate-200 rounded-lg text-slate-500 dark:bg-slate-800/80 dark:hover:bg-slate-700 dark:text-slate-300 transition"
                              title="View"
                            >
                              <FaEye />
                            </button>

                            {adv.status === "DRAFT" && (
                              <>
                                <button
                                  type="button"
                                  onClick={() => openApproveModal(adv.id)}
                                  className="p-2 bg-slate-100 text-slate-500 rounded-lg hover:text-emerald-600 hover:bg-emerald-50 dark:bg-slate-800 dark:text-slate-300 dark:hover:text-emerald-400 dark:hover:bg-emerald-950/40 transition-colors"
                                  title="Approve"
                                >
                                  <FaCheck className="text-xs" />
                                </button>

                                <button
                                  type="button"
                                  onClick={() => openModal("edit", adv)}
                                  className="p-2 bg-blue-50 hover:bg-blue-100 rounded-lg text-blue-600 border border-blue-100 dark:bg-blue-600/10 dark:hover:bg-blue-600/20 dark:text-blue-400 dark:border-blue-500/10 transition"
                                  title="Edit"
                                >
                                  <FaEdit />
                                </button>

                                <button
                                  type="button"
                                  onClick={() => setDeleteTargetId(adv.id)}
                                  className="p-2 bg-red-50 hover:bg-red-100 rounded-lg text-red-600 border border-red-100 dark:bg-red-600/10 dark:hover:bg-red-600/20 dark:text-red-400 dark:border-red-500/10 transition"
                                  title="Delete"
                                >
                                  <FaTrash />
                                </button>
                              </>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Bottom Sheet Modal */}
        <div
          className={`fixed inset-0 z-[2000] flex flex-col justify-end transition-opacity duration-300 ${
            isModalOpen
              ? "opacity-100 pointer-events-auto"
              : "opacity-0 pointer-events-none"
          }`}
        >
          <div
            className="absolute inset-0 bg-slate-900/40 dark:bg-slate-950/70 backdrop-blur-sm"
            onClick={() => setIsModalOpen(false)}
          />

          <div
            className={`relative w-full max-w-xl mx-auto bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/10 rounded-t-3xl p-5 sm:p-8 shadow-2xl transition-transform duration-300 transform space-y-5 max-h-[90vh] overflow-y-auto ${
              isModalOpen ? "translate-y-0" : "translate-y-full"
            }`}
          >
            <div className="flex justify-between items-start">
              <div>
                <h3 className="text-lg font-bold text-slate-900 dark:text-white capitalize">
                  {modalMode === "create"
                    ? "Give Advance"
                    : modalMode === "edit"
                    ? "Edit Advance"
                    : "Advance Details"}
                </h3>

                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                  {modalMode === "view"
                    ? "Read-only view."
                    : "Fill in the details below."}
                </p>
              </div>

              <button
                onClick={() => setIsModalOpen(false)}
                className="p-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-500 border border-slate-200 dark:bg-white/5 dark:hover:bg-white/10 dark:text-slate-300 dark:border-white/5 transition-colors"
              >
                <FaTimes className="text-sm" />
              </button>
            </div>

            {/* View */}
            {modalMode === "view" && selectedAdvance && (
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div className="bg-slate-50 rounded-xl p-3 border border-slate-200 dark:bg-slate-950/50 dark:border-white/5">
                  <div className="text-slate-400 dark:text-slate-500 mb-1">
                    Employee
                  </div>
                  <div className="text-slate-800 dark:text-white font-semibold">
                    {employeeLabel(selectedAdvance)}
                  </div>
                </div>

                <div className="bg-slate-50 rounded-xl p-3 border border-slate-200 dark:bg-slate-950/50 dark:border-white/5">
                  <div className="text-slate-400 dark:text-slate-500 mb-1">
                    Status
                  </div>
                  <div>{statusBadge(selectedAdvance.status)}</div>
                </div>

                <div className="bg-slate-50 rounded-xl p-3 border border-slate-200 dark:bg-slate-950/50 dark:border-white/5">
                  <div className="text-slate-400 dark:text-slate-500 mb-1">
                    Amount
                  </div>
                  <div className="text-slate-800 dark:text-white font-semibold">
                    {formatMoney(selectedAdvance.amount)}
                  </div>
                </div>

                <div className="bg-slate-50 rounded-xl p-3 border border-slate-200 dark:bg-slate-950/50 dark:border-white/5">
                  <div className="text-slate-400 dark:text-slate-500 mb-1">
                    Balance
                  </div>
                  <div className="text-amber-600 dark:text-amber-300 font-semibold">
                    {formatMoney(selectedAdvance.balance)}
                  </div>
                </div>

                <div className="bg-slate-50 rounded-xl p-3 border border-slate-200 dark:bg-slate-950/50 dark:border-white/5 col-span-2">
                  <div className="text-slate-400 dark:text-slate-500 mb-1">
                    Reason
                  </div>
                  <div className="text-slate-700 dark:text-slate-200">
                    {selectedAdvance.reason || "—"}
                  </div>
                </div>

                {selectedAdvance.advanceDate && (
                  <div className="bg-slate-50 rounded-xl p-3 border border-slate-200 dark:bg-slate-950/50 dark:border-white/5 col-span-2 flex items-center gap-2">
                    <FaCalendarAlt className="text-slate-400 dark:text-slate-500" />
                    <span className="text-slate-600 dark:text-slate-300">
                      {selectedAdvance.advanceDate}
                    </span>
                  </div>
                )}

                {selectedAdvance.status === "DRAFT" && (
                  <div className="col-span-2 pt-2">
                    <button
                      type="button"
                      onClick={() => {
                        setIsModalOpen(false);
                        openApproveModal(selectedAdvance.id);
                      }}
                      className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 dark:hover:bg-emerald-500 text-white text-xs font-semibold transition"
                    >
                      <FaCheck />
                      Approve Advance
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Create / Edit */}
            {modalMode !== "view" && (
              <form onSubmit={handleSave} className="space-y-5">
                {modalMode === "create" && (
                  <div className="flex flex-col gap-1.5" ref={empDropdownRef}>
                    <label className={labelClass}>
                      <FaUser className="text-[10px] text-slate-400" />
                      Employee
                    </label>

                    <button
                      type="button"
                      onClick={() => setEmpDropdownOpen((o) => !o)}
                      className={`${fieldClass} flex items-center justify-between gap-2 text-left`}
                    >
                      <span
                        className={
                          formData.employeeId
                            ? "text-slate-800 dark:text-white"
                            : "text-slate-400 dark:text-slate-600"
                        }
                      >
                        {formData.employeeId
                          ? selectedEmployeeLabel()
                          : "Search & select employee..."}
                      </span>

                      <FaChevronDown
                        className={`text-slate-400 dark:text-slate-500 text-[10px] transition-transform ${
                          empDropdownOpen ? "rotate-180" : ""
                        }`}
                      />
                    </button>

                    {empDropdownOpen && (
                      <div className="relative z-10">
                        <div className="absolute left-0 right-0 top-1 bg-white border border-slate-200 rounded-xl shadow-2xl dark:bg-slate-900 dark:border-white/10 overflow-hidden">
                          <div className="p-2 border-b border-slate-200 bg-slate-50 dark:border-white/5 dark:bg-slate-950/70">
                            <div className="relative">
                              <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500 text-[10px]" />

                              <input
                                type="text"
                                autoFocus
                                placeholder="Type name or number..."
                                value={empSearch}
                                onChange={(e) => setEmpSearch(e.target.value)}
                                className="w-full bg-white border border-slate-200 rounded-lg pl-8 pr-3 py-2 text-xs text-slate-800 placeholder-slate-400 focus:outline-none focus:border-emerald-500/40 dark:bg-slate-950/80 dark:border-white/10 dark:text-white dark:placeholder-slate-600"
                              />
                            </div>
                          </div>

                          <ul className="max-h-48 overflow-y-auto py-1">
                            {filteredEmployees.length === 0 ? (
                              <li className="px-4 py-3 text-xs text-slate-400 dark:text-slate-500 text-center">
                                No employees found
                              </li>
                            ) : (
                              filteredEmployees.map((emp) => {
                                const label =
                                  emp.fullName || emp.name || `#${emp.id}`;

                                const sub = emp.employeeNumber
                                  ? emp.employeeNumber
                                  : null;

                                const isSelected =
                                  String(formData.employeeId) ===
                                  String(emp.id);

                                return (
                                  <li key={emp.id}>
                                    <button
                                      type="button"
                                      onClick={() => selectEmployee(emp)}
                                      className={`w-full text-left px-4 py-2.5 text-xs transition flex items-center justify-between gap-2 ${
                                        isSelected
                                          ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-600/20 dark:text-emerald-300"
                                          : "text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-white/[0.03]"
                                      }`}
                                    >
                                      <span className="font-medium truncate">
                                        {label}
                                      </span>

                                      {sub && (
                                        <span className="text-[10px] text-slate-400 dark:text-slate-500 shrink-0">
                                          {sub}
                                        </span>
                                      )}
                                    </button>
                                  </li>
                                );
                              })
                            )}
                          </ul>
                        </div>
                      </div>
                    )}

                    <input
                      type="text"
                      required
                      value={formData.employeeId}
                      onChange={() => {}}
                      className="sr-only"
                      tabIndex={-1}
                    />
                  </div>
                )}

                {/* Amount */}
                <div className="flex flex-col gap-1.5">
                  <label className={labelClass}>
                    <FaMoneyBillWave className="text-[10px] text-slate-400" />
                    Amount
                  </label>

                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    required={modalMode === "create"}
                    placeholder="e.g. 50000"
                    value={formData.amount}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        amount: e.target.value,
                      })
                    }
                    className={fieldClass}
                  />
                </div>

                {/* Reason */}
                <div className="flex flex-col gap-1.5">
                  <label className={labelClass}>Reason</label>

                  <input
                    type="text"
                    placeholder="e.g. Emergency, Salary advance"
                    value={formData.reason}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        reason: e.target.value,
                      })
                    }
                    className={fieldClass}
                  />
                </div>

                {/* Advance Rate */}
                <div className="flex flex-col gap-1.5">
                  <label className={labelClass}>
                    <FaMoneyBillWave className="text-[10px] text-slate-400" />
                    Monthly Recovery Rate
                    <span className="ml-1 font-normal text-slate-400 normal-case tracking-normal">
                      (e.g. 0.25 = recover in 4 months)
                    </span>
                  </label>

                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    required={modalMode === "create"}
                    placeholder="e.g. 0.5"
                    value={formData.advanceRate}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        advanceRate: e.target.value,
                      })
                    }
                    className={fieldClass}
                  />
                </div>

                {/* Status */}
                {modalMode === "edit" && (
                  <div className="flex flex-col gap-1.5">
                    <label className={labelClass}>Status</label>

                    <select
                      value={formData.status}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          status: e.target.value,
                        })
                      }
                      className={fieldClass}
                    >
                      <option value="DRAFT">DRAFT</option>
                      <option value="ACTIVE">ACTIVE</option>
                      <option value="RECOVERED">RECOVERED</option>
                      <option value="CANCELLED">CANCELLED</option>
                    </select>
                  </div>
                )}

                {/* Actions */}
                <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 pt-4 border-t border-slate-200 dark:border-white/5">
                  <button
                    type="button"
                    onClick={() => setIsModalOpen(false)}
                    className="px-4 py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-600 dark:bg-white/5 dark:hover:bg-white/10 dark:text-slate-300 text-xs font-semibold transition"
                  >
                    Close
                  </button>

                  <button
                    type="submit"
                    className="px-5 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 dark:hover:bg-emerald-500 text-white text-xs font-semibold shadow-md shadow-emerald-600/10 transition"
                  >
                    {modalMode === "create" ? "Give Advance" : "Apply Changes"}
                  </button>
                </div>
              </form>
            )}

            {modalMode === "view" && selectedAdvance?.status !== "DRAFT" && (
              <div className="flex justify-end pt-2 border-t border-slate-200 dark:border-white/5">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-600 dark:bg-white/5 dark:hover:bg-white/10 dark:text-slate-300 text-xs font-semibold transition"
                >
                  Close
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Approve Confirmation Modal */}
        <div
          className={`fixed inset-0 z-[2100] flex items-center justify-center p-4 transition-opacity duration-200 ${
            approveTargetId
              ? "opacity-100 pointer-events-auto"
              : "opacity-0 pointer-events-none"
          }`}
        >
          <div
            className="absolute inset-0 bg-slate-900/40 dark:bg-slate-950/70 backdrop-blur-sm"
            onClick={closeApproveModal}
          />

          <div className="relative w-full max-w-sm bg-white border border-slate-200 rounded-2xl p-5 shadow-2xl dark:bg-slate-900 dark:border-white/10 space-y-4">
            <div className="flex justify-between items-start">
              <div>
                <h3 className="text-base font-bold text-slate-900 dark:text-white">
                  Approve Advance
                </h3>

                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                  This cannot be undone. Choose how the advance is paid out.
                </p>
              </div>

              <button
                type="button"
                onClick={closeApproveModal}
                disabled={isApproving}
                className="p-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-500 dark:bg-white/5 dark:hover:bg-white/10 dark:text-slate-300 transition disabled:opacity-50"
              >
                <FaTimes className="text-sm" />
              </button>
            </div>

            <div className="flex flex-col gap-1.5">
              <label className={labelClass}>Payment Method</label>

              <select
                value={approveMethod}
                onChange={(e) => setApproveMethod(e.target.value)}
                disabled={isApproving}
                className={fieldClass}
              >
                <option value="CASH">CASH</option>
                <option value="BANK">BANK</option>
              </select>
            </div>

            {error && approveTargetId && (
              <div className="p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 dark:bg-red-500/10 dark:border-red-500/20 dark:text-red-400 text-xs font-medium">
                {error}
              </div>
            )}

            <div className="flex gap-2 pt-1">
              <button
                type="button"
                onClick={closeApproveModal}
                disabled={isApproving}
                className="flex-1 px-4 py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-600 dark:bg-white/5 dark:hover:bg-white/10 dark:text-slate-300 text-xs font-semibold transition disabled:opacity-50"
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={handleApproveAdvance}
                disabled={isApproving}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 dark:hover:bg-emerald-500 text-white text-xs font-semibold transition disabled:opacity-50"
              >
                {isApproving ? (
                  <>
                    <FaSpinner className="animate-spin" />
                    Approving...
                  </>
                ) : (
                  <>
                    <FaCheck />
                    Approve
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmployeeAdvanceManagement;
